// useLiveResource — single hook that captures the SPA's standard pattern
// for keeping a React state slot in sync with a server-side resource
// across:
//
//   1. initial REST fetch on mount / key change
//   2. live updates via a WebSocket event
//   3. re-fetch on every WS reconnect (so a long-lived tab survives an
//      orchestrator pod restart — the new orchestrator's empty
//      snapshot cache won't replay anything, but the re-fetch hydrates
//      from REST)
//   4. optionally: re-fetch when a specific profile's backend supervisor
//      transitions to `running` (covers the case where the initial
//      fetch lazy-started the backend but raced ahead of it, so the
//      first response came back empty / error)
//
// Before this hook existed, ProfileDetail, Mt5RuntimeSection,
// WorkspacePage, and TelegramLoginCard each reimplemented some subset
// of (1)–(4), often forgetting one of them and producing the kind of
// "tab shows stale state forever after orchestrator restart" bugs that
// motivated this consolidation.

import { useEffect, useRef, useState } from "react";
import { wsClient } from "../client/ws-client";

export interface UseLiveResourceOptions<T> {
	/**
	 * Stable identifier for the resource (typically a profileId). When
	 * it changes, the hook resets and refetches from scratch. Pass
	 * `null` to disable the hook entirely (returns `{ value: null }`).
	 */
	key: string | null;

	/**
	 * REST fetcher invoked on mount, on every WS reconnect, and when
	 * any `refetch*` trigger fires. Should reject (not return null)
	 * on error so the caller's catch path runs. Captured via ref —
	 * inline arrow functions are fine, the hook doesn't re-run the
	 * effect when this changes.
	 */
	fetcher: () => Promise<T>;

	/**
	 * Optional WS subscription for live updates. Called once per `key`
	 * change; the implementation should filter by `key` itself (e.g.,
	 * `if (incomingProfileId !== key) return`) before invoking
	 * `onUpdate`. Returns an unsubscribe function.
	 */
	subscribe?: (onUpdate: (value: T) => void) => () => void;

	/**
	 * When set, also refetch when the named profile's backend supervisor
	 * state transitions to `running`. Use for resources whose REST
	 * endpoint depends on the backend child process — settings, telegram
	 * session, channel parser results, etc.
	 */
	refetchOnBackendRunning?: { profileId: string };

	/**
	 * Whether the catch-on-fetch-error path should clear the value
	 * (default `false`, errors leave the previous value intact). Set
	 * `true` for cases where you want a clean reset on any failure.
	 */
	clearOnError?: boolean;
}

export interface UseLiveResourceResult<T> {
	value: T | null;
	error: unknown;
	/** Trigger a fresh REST fetch. Useful after a successful mutation. */
	refetch: () => void;
	/**
	 * Imperatively overwrite the cached value. Use when an action handler
	 * (form submit, logout, etc.) has the new value in hand and wants to
	 * reflect it locally without waiting for the WS event round-trip.
	 * The next WS update / refetch overwrites this — they're equally
	 * valid sources of truth and the most-recent always wins.
	 */
	setValue: (next: T | null) => void;
}

export function useLiveResource<T>(
	options: UseLiveResourceOptions<T>,
): UseLiveResourceResult<T> {
	const {
		key,
		fetcher,
		subscribe,
		refetchOnBackendRunning,
		clearOnError = false,
	} = options;
	const [value, setValue] = useState<T | null>(null);
	const [error, setError] = useState<unknown>(null);
	const fetcherRef = useRef(fetcher);
	fetcherRef.current = fetcher;
	const subscribeRef = useRef(subscribe);
	subscribeRef.current = subscribe;
	const refetchTriggerRef = useRef<() => void>(() => {});

	useEffect(() => {
		if (!key) {
			setValue(null);
			setError(null);
			refetchTriggerRef.current = () => {};
			return;
		}

		let cancelled = false;
		setError(null);

		const fetchAndSet = () => {
			void fetcherRef
				.current()
				.then((next) => {
					if (cancelled) return;
					setValue(next);
				})
				.catch((err) => {
					if (cancelled) return;
					setError(err);
					if (clearOnError) setValue(null);
				});
		};

		refetchTriggerRef.current = fetchAndSet;
		fetchAndSet();

		const unsubs: Array<() => void> = [];
		if (subscribeRef.current) {
			unsubs.push(
				subscribeRef.current((next) => {
					if (cancelled) return;
					setValue(next);
				}),
			);
		}
		unsubs.push(
			wsClient.onConnected(() => {
				if (!cancelled) fetchAndSet();
			}),
		);
		if (refetchOnBackendRunning) {
			const watchedProfileId = refetchOnBackendRunning.profileId;
			unsubs.push(
				wsClient.onBackendChanged((incomingProfileId, state) => {
					if (cancelled) return;
					if (incomingProfileId !== watchedProfileId) return;
					if (state.status === "running") fetchAndSet();
				}),
			);
		}

		return () => {
			cancelled = true;
			for (const u of unsubs) u();
		};
		// `key` is the only declared dep; `fetcher` / `subscribe` are
		// intentionally not in the dep array (callers commonly pass
		// inline arrows). They're captured via refs above so the most
		// recent version is always invoked.
	}, [key, refetchOnBackendRunning?.profileId, clearOnError]);

	return {
		value,
		error,
		refetch: () => refetchTriggerRef.current(),
		setValue,
	};
}
